import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Global theme provider. Dark is the default. Preference persists to
 * localStorage so a return visit stays on the coach's chosen theme.
 *
 * Applies the `.light` class to <html> when active — index.css contains
 * the `:root.light` variant with light-mode CSS variables + overrides for
 * hardcoded dark-utility classes so most of the app just works.
 */
const THEME_KEY = "pbcoach.theme";
const ThemeContext = createContext({ theme: "dark", setTheme: () => {}, toggle: () => {} });

function readInitialTheme() {
  if (typeof window === "undefined") return "dark";
  try {
    const stored = window.localStorage.getItem(THEME_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch { /* SSR / private mode */ }
  return "dark"; // default
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readInitialTheme);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "light") root.classList.add("light");
    else root.classList.remove("light");
    // Nudge the browser UI (address bar) to match the chrome
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "light" ? "#F8F9FB" : "#0A0A0A");
    try { window.localStorage.setItem(THEME_KEY, theme); } catch { /* */ }
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next === "light" ? "light" : "dark");
  }, []);
  const toggle = useCallback(() => {
    setThemeState((cur) => (cur === "light" ? "dark" : "light"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
